import {
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, startWith, switchMap } from 'rxjs';
import { ContentType } from '../../pages/content-creation/content-creation';
import { MediaBlockService } from '../../services/media-block-service';
import { SelectComponent } from '@kinomap-dev/ng-ui-lib/forms';
import { getWorkoutModes, WORKOUT_SPORT_TYPES, WorkoutSportType } from '../../types/sport.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { Tooltip } from '../../directives/tooltip.directive';
import { ModalService } from '../../services/modal-service';
import { UpperFirstLetterPipe } from '../../pipes/upper-first-letter-pipe';
import { SectionsEditor } from '../sections-editor/sections-editor';
import { SectionModelType } from '../../types/section.type';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { CreateContentService } from '../../services/create-content.service';
import { SelectField } from '../select-field/select-field';
import { CoachingIntervalsEditor } from '../coaching-intervals-editor/coaching-intervals-editor';

@Component({
  selector: 'app-training-block',
  imports: [
    SelectComponent,
    TranslateModule,
    UpperFirstLetterPipe,
    SectionsEditor,
    NgTemplateOutlet,
    IconComponent,
    Tooltip,
    SelectField,
    CoachingIntervalsEditor,
  ],
  templateUrl: './training-block.html',
  styleUrl: './training-block.scss',
})
export class TrainingBlock {
  public mediaBlockService: MediaBlockService = inject(MediaBlockService);
  readonly contentType: InputSignal<ContentType | undefined> = input<ContentType | undefined>();
  /** Formulaire du sport, du mode et des sections. */
  trainingForm: InputSignal<FormGroup | undefined> = input<FormGroup>();
  /** Formulaire de la video du coaching, absent en workout. */
  readonly mediaForm: InputSignal<FormGroup | undefined> = input<FormGroup>();
  readonly fullscreen: InputSignal<boolean> = input<boolean>(false);
  protected readonly sportTypes = WORKOUT_SPORT_TYPES;

  private createContentService: CreateContentService = inject(CreateContentService);
  private modalService: ModalService = inject(ModalService);
  private translateService: TranslateService = inject(TranslateService);

  protected sections: WritableSignal<SectionModelType[]> =
    this.createContentService.workoutSections;
  protected selectedSectionsIds: WritableSignal<number[]> =
    this.createContentService.workoutSelectedSectionsIds;

  constructor() {
    effect(() => {
      if (this.contentType() !== 'workout') return;
      this.createContentService.workoutMode.set(this.workoutMode());
    });

    effect(() => {
      const mediaForm: FormGroup | undefined = this.mediaForm();
      const contentType: ContentType | undefined = this.contentType();
      if (!mediaForm || !contentType) return;
      this.mediaBlockService.mediaForm = mediaForm;
      this.mediaBlockService.contentType = contentType;
    });

    effect(() => {
      this.createContentService.workoutImportedFile();
      untracked(() => this.selectsRevision.update((revision: number) => revision + 1));
    });
  }

  private readonly formValue: Signal<Record<string, unknown> | undefined> = toSignal(
    toObservable(this.trainingForm).pipe(
      switchMap((form: FormGroup | undefined) =>
        form ? form.valueChanges.pipe(startWith(form.value)) : of(undefined),
      ),
    ),
  );

  sport: Signal<WorkoutSportType | undefined> = computed(
    () => (this.formValue()?.['sport'] as WorkoutSportType | undefined) ?? undefined,
  );

  workoutModes: Signal<WorkoutMode[] | undefined> = computed(() => getWorkoutModes(this.sport()));

  workoutMode: Signal<WorkoutMode | undefined> = computed(
    () => (this.formValue()?.['workoutMode'] as WorkoutMode | undefined) ?? undefined,
  );

  disabledEditor: Signal<boolean> = computed(() => !this.sport() || !this.workoutMode());

  /** Incremente pour recreer les selects et leur faire reafficher la valeur du formulaire. */
  protected readonly selectsRevision: WritableSignal<number> = signal(0);

  changeSport(sport: string): void {
    this.confirmSectionsReset(() => {
      this.trainingForm()?.get('sport')?.setValue(sport);
      this.trainingForm()?.get('workoutMode')?.setValue(undefined);
      this.sections.set([]);
    });
  }

  changeWorkoutMode(workoutMode: string): void {
    this.confirmSectionsReset(() => {
      this.trainingForm()?.get('workoutMode')?.setValue(workoutMode);
      this.sections.set([]);
    });
  }

  private confirmSectionsReset(reset: () => void): void {
    if (!this.sections().length) {
      reset();
      return;
    }

    this.modalService
      .openConfirmation<'confirm' | 'cancel'>({
        title: 'confirm_reset_sections.title',
        message: 'confirm_reset_sections.message',
        buttons: [
          { action: 'cancel', label: 'common.cancel', style: 'secondary' },
          { action: 'confirm', label: 'common.validate', style: 'primary' },
        ],
      })
      .closed.subscribe((action: 'confirm' | 'cancel' | undefined) => {
        if (action === 'confirm') reset();
        else this.selectsRevision.update((revision: number) => revision + 1);
      });
  }

  openFullscreenModal(): void {
    this.modalService.openModal<{
      contentType: ContentType | undefined;
      trainingForm: FormGroup | undefined;
      mediaForm: FormGroup | undefined;
      fullscreen: boolean;
    }>(TrainingBlock, '100%', '100vh', this.translateService.instant('training_block.header'), {
      contentType: this.contentType(),
      trainingForm: this.trainingForm(),
      mediaForm: this.mediaForm(),
      fullscreen: true,
    });
  }
}
